import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const StableContext = createContext(null);
export const useStable = () => useContext(StableContext);

export function StableProvider({ children }) {
  const { user } = useAuth();
  const [stables, setStables] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assist, setAssist] = useState(false);

  const refresh = useCallback(async () => {
    // Platform Admin assistance mode: load one stable read-only, outside membership.
    const assistId = localStorage.getItem("equiro_assist");
    if (assistId) {
      try {
        const { data } = await api.get(`/stables/${assistId}`);
        setStables([]);
        setCurrent({ ...data, assist: true });
        setAssist(true);
        setLoading(false);
        return [];
      } catch { localStorage.removeItem("equiro_assist"); }
    }
    setAssist(false);
    const { data } = await api.get("/stables");
    setStables(data);
    const savedId = localStorage.getItem("equiro_stable");
    const found = data.find((s) => s.id === savedId) || null;
    setCurrent((prev) => {
      if (prev && !prev.assist) {
        const updated = data.find((s) => s.id === prev.id);
        return updated || found;
      }
      if (found) return found;
      if (data.length === 1) {
        localStorage.setItem("equiro_stable", data[0].id);
        return data[0];
      }
      return null;
    });
    setLoading(false);
    return data;
  }, []);

  useEffect(() => {
    if (user && user.id) refresh();
  }, [user, refresh]);

  const select = (stable) => {
    setCurrent(stable);
    if (stable) localStorage.setItem("equiro_stable", stable.id);
  };

  const exitAssist = async () => {
    const id = localStorage.getItem("equiro_assist");
    localStorage.removeItem("equiro_assist");
    if (id) { try { await api.post(`/admin/stables/${id}/assistance/exit`); } catch { /* noop */ } }
    setAssist(false);
  };

  const role = current?.role;
  const perms = {
    canWrite: !assist && ["staff", "manager", "admin"].includes(role),
    canManage: !assist && ["manager", "admin"].includes(role),
    isOwner: role === "owner",
    canOps: !assist && ["owner", "staff", "manager", "admin"].includes(role),
    isPlatformAdmin: !!user?.platform_admin,
    assist,
  };

  return (
    <StableContext.Provider value={{ stables, current, select, refresh, loading, role, perms, assist, exitAssist }}>
      {children}
    </StableContext.Provider>
  );
}
